import type { ReactNode } from 'react';
import { Send, UserMinus, Users } from 'lucide-react';
import type { JobMobileState } from './use-job-mobile';
import type { PaigeMobileSender } from './mobile-types';
import { capturedWhen, senderState } from './mobile-words';
import { MobileAddPeople } from './MobileAddPeople';

/**
 * Who can send to this job from their phone: one quiet line for the company's own people (they already
 * can), the add box, and the people as rows, each with their state and when they last sent, a new invite
 * and off this job. Removing someone from the company is People's (Company › People), not the job's.
 */
export function MobilePeople({ state }: { state: JobMobileState }): ReactNode {
  const mobile = state.mobile;
  const runsJobs = window.paigeWorkbench?.runsJobs !== false;
  return (
    <section className="export-stage paige-phone-people" aria-label="Who can send">
      <header className="export-stage-head">
        <Users size={18} aria-hidden="true" />
        <h3>Who can send</h3>
        {mobile?.seats.limit != null && (
          <span className="export-stage-subtitle">
            {mobile.seats.used} of {mobile.seats.limit} seats
          </span>
        )}
      </header>
      <p className="export-stage-caption">Everyone with Paige at your company already can.</p>
      {mobile === undefined ? (
        <p className="export-stage-caption">Getting this job’s people…</p>
      ) : (
        mobile && (
          <>
            {runsJobs && <MobileAddPeople state={state} />}
            {mobile.senders.length > 0 && (
              <ul className="paige-mobile-list" aria-label="Who can send from a phone">
                {mobile.senders.map((sender) => (
                  <SenderRow key={sender.id} sender={sender} state={state} runsJobs={runsJobs} />
                ))}
              </ul>
            )}
          </>
        )
      )}
    </section>
  );
}

function SenderRow(props: {
  sender: PaigeMobileSender;
  state: JobMobileState;
  runsJobs: boolean;
}): ReactNode {
  const { sender } = props;
  return (
    <li className="paige-mobile-row">
      <span className="paige-mobile-avatar" aria-hidden="true">
        {initials(sender)}
      </span>
      <span className="paige-mobile-who">
        <strong>{sender.name ?? sender.email}</strong>
        {sender.name && <small>{sender.email}</small>}
      </span>
      <span className={`paige-mobile-status is-${sender.status}`}>{senderState(sender)}</span>
      <span className="paige-mobile-sent">{lastSent(sender)}</span>
      {props.runsJobs && <SenderActions sender={sender} state={props.state} />}
    </li>
  );
}

function SenderActions({
  sender,
  state,
}: {
  sender: PaigeMobileSender;
  state: JobMobileState;
}): ReactNode {
  const who = sender.name ?? sender.email;
  return (
    <span className="paige-mobile-actions">
      {sender.status !== 'active' && (
        <button
          type="button"
          className="quiet-button small"
          disabled={state.saving}
          title="Send a new invite"
          aria-label={`Send ${who} a new invite`}
          onClick={() => void state.resend(sender.id)}
        >
          <Send size={14} aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className="quiet-button small"
        disabled={state.saving}
        title="Remove from this job"
        aria-label={`Remove ${who} from this job`}
        onClick={() => void state.remove(sender.id)}
      >
        <UserMinus size={14} aria-hidden="true" />
      </button>
    </span>
  );
}

/** "Last sent Oct 4, 9:12 AM", or "Nothing sent yet". */
function lastSent(sender: PaigeMobileSender): string {
  return sender.lastSentAt ? `Last sent ${capturedWhen(sender.lastSentAt)}` : 'Nothing sent yet';
}

/** "JS" for Jane Smith, "J" for jane@acme.com. */
function initials(sender: PaigeMobileSender): string {
  const words = (sender.name ?? sender.email).split(/[\s@.]+/).filter(Boolean);
  const letters = sender.name ? words.slice(0, 2) : words.slice(0, 1);
  return letters.map((word) => word[0]?.toUpperCase() ?? '').join('');
}
